/**
 * Messages: the clinic inbox (Treatment Plan spec v1.4, S-13; canvas board
 * 10). Patient conversations, separate from Plan Questions, which stay in
 * Ready to Prescribe. Tabs by status (MS-007), clinical conversations first
 * and in red (MS-005), the wait coloured from the clinic's thresholds
 * (MS-009). A reply passes the wording and timeline checks while it is typed
 * and again on send (PG-020). Internal notes are yellow and never reach the
 * patient (MS-011).
 *
 * The delivery log of what MCOS sent lives at /messages/log.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, ErrorState, InfoHint, LoadingRows, PageHeader, inputClass } from '../components/ui';
import { PhotoThumb } from '../components/PhotoThumb';
import { RecordSideEffect } from '../components/SideEffects';

type Tab = 'needs_reply' | 'with_pharmacy' | 'waiting_on_patient' | 'closed';
interface Row {
  id: string;
  patientId: string;
  patientName: string;
  initials: string;
  topicLabel: string;
  clinical: boolean;
  preview: string;
  prescriber: string | null;
  waitMinutes: number | null;
  wait: 'none' | 'green' | 'amber' | 'red';
}
interface InboxResponse {
  counts: Record<Tab, number>;
  threads: Row[];
}
interface Detail {
  id: string;
  patientId: string;
  patientName: string;
  patientLine: string;
  patientPhone: string | null;
  topicLabel: string;
  clinical: boolean;
  status: string;
  contactPref: string;
  prescriber: string | null;
  waitMinutes: number | null;
  wait: Row['wait'];
  messages: Array<{ id: string; from: string; sender: string; internal: boolean; body: string; at: string; readByPatient: boolean; photos?: Array<{ id: string; mime: string }> }>;
}

const TABS: Array<{ key: Tab; text: string }> = [
  { key: 'needs_reply', text: 'Needs reply' },
  { key: 'with_pharmacy', text: 'With pharmacy' },
  { key: 'waiting_on_patient', text: 'Waiting on patient' },
  { key: 'closed', text: 'Closed' },
];
const STATUS_TEXT: Record<string, string> = {
  waiting_on_clinic: 'Needs reply',
  with_pharmacy: 'With pharmacy',
  waiting_on_patient: 'Waiting on patient',
  closed: 'Closed',
};
const WAIT_TEXT = { none: 'text-[#5b6b7c]', green: 'text-forest', amber: 'text-risk-yellow', red: 'text-alert' } as const;
const chip = 'inline-flex items-center rounded-[5px] px-2 py-[3px] text-[11.5px]';
const label = 'text-[11px] font-semibold uppercase tracking-[0.14em] text-navy';
const waited = (m: number | null) => (m == null ? '' : m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${Math.floor(m / 1440)}d`);
const at = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function Initials({ text }: { text: string }) {
  return <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#e3efe9] font-display text-[12px] font-semibold text-navy">{text}</span>;
}

export function MessagesInboxPage() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'needs_reply') as Tab;
  const inbox = useQuery({ queryKey: ['conversations', tab], queryFn: () => api.get<InboxResponse>(`/conversations?tab=${tab}`), refetchInterval: 60_000 });
  const [selected, setSelected] = useState<string | null>(null);
  const rows = inbox.data?.threads ?? [];
  useEffect(() => {
    if ((!selected || !rows.some((r) => r.id === selected)) && rows[0]) setSelected(rows[0].id);
    if (!rows.length) setSelected(null);
  }, [rows, selected]);

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader title="Messages" subtitle="Patient conversations. Plan questions stay in Ready to Prescribe." />
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex flex-wrap gap-1 rounded-[12px] bg-[#ecebe5] p-1" role="tablist" aria-label="Messages">
          {TABS.map((t) => {
            const count = inbox.data?.counts[t.key];
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setParams(t.key === 'needs_reply' ? {} : { tab: t.key }, { replace: true })}
                className={`flex h-[42px] items-center gap-2 rounded-[9px] px-4 text-[15px] ${tab === t.key ? 'bg-white font-semibold text-navy shadow-sm' : 'text-[#4d6072] hover:text-navy'}`}
              >
                {t.text}
                {count != null && count > 0 && t.key !== 'closed' && t.key !== 'waiting_on_patient' && (
                  <span className={`grid h-[22px] min-w-[22px] place-items-center rounded-full px-1.5 text-[11.5px] font-semibold text-white ${t.key === 'with_pharmacy' ? 'bg-[#8a6a2a]' : 'bg-forest'}`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
        <Link to="/messages/log" className="text-[13.5px] font-semibold text-forest underline">What MCOS sent</Link>
      </div>

      {inbox.isLoading && <LoadingRows rows={4} />}
      {inbox.error && <ErrorState error={inbox.error} retry={() => void inbox.refetch()} />}
      {inbox.data && rows.length === 0 && (
        <Card>
          <EmptyState
            title={tab === 'needs_reply' ? 'No conversation needs a reply' : 'Nothing here'}
            body="When a patient writes to the care team from the app, the conversation waits here, longest wait first."
          />
        </Card>
      )}
      {rows.length > 0 && (
        <div className="grid items-start gap-5 lg:grid-cols-[400px_minmax(0,1fr)]">
          <Card padded={false}>
            <div className="border-b border-hairline bg-[#f3f2ed] px-4 py-3"><span className={label}>{tab === 'closed' ? 'Most recent first' : 'Longest waiting first'}</span></div>
            <ul className="divide-y divide-hairline" data-testid="conversation-queue">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-pressed={selected === row.id}
                    className={`flex w-full gap-3 border-l-[3px] px-4 py-4 text-left ${selected === row.id ? 'border-forest bg-mint/50' : 'border-transparent hover:bg-mint/25'}`}
                  >
                    <Initials text={row.initials} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="font-display text-[16px] font-semibold text-navy">{row.patientName}</span>
                        <span className={`shrink-0 text-[12px] font-semibold ${WAIT_TEXT[row.wait]}`}>{waited(row.waitMinutes)}</span>
                      </span>
                      <span className="mt-1.5 block truncate text-[14px] text-navy">{row.preview}</span>
                      <span className="mt-1.5 flex flex-wrap gap-1.5">
                        <span className={`${chip} font-semibold ${row.clinical ? 'bg-alert-soft text-alert' : 'bg-mint text-forest'}`}>{row.topicLabel}</span>
                        {row.prescriber && <span className={`${chip} bg-[#efeee9] text-[#4d6072]`}>{row.prescriber}</span>}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          {selected && <ConversationDetail key={selected} id={selected} />}
        </div>
      )}
    </div>
  );
}

function ConversationDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['conversation', id], queryFn: () => api.get<Detail>(`/conversations/${id}`) });
  const [text, setText] = useState('');
  const [checked, setChecked] = useState<Array<{ phrase: string; why: string }> | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  // PG-020 while typing: the same check the send runs.
  useEffect(() => {
    if (!text.trim()) return setChecked(null);
    const timer = window.setTimeout(() => {
      api.post<{ violations: Array<{ phrase: string; why: string }> }>('/conversations/check', { text }).then((r) => setChecked(r.violations)).catch(() => setChecked(null));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [text]);

  const done = (message: string) => {
    setText('');
    setFlash(message);
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    void qc.invalidateQueries({ queryKey: ['conversation', id] });
    void qc.invalidateQueries({ queryKey: ['nav-counts'] });
  };
  const replyM = useMutation({
    mutationFn: (channel: 'portal' | 'call') => api.post<Detail>(`/conversations/${id}/reply`, { body: text, channel }),
    onSuccess: (_d, channel) => done(channel === 'call' ? 'Sent. The patient reads that you will call.' : 'Reply sent. The patient reads it in the app.'),
  });
  const note = useMutation({ mutationFn: () => api.post<Detail>(`/conversations/${id}/note`, { body: text }), onSuccess: () => done('Internal note added. The patient never sees it.') });
  const close = useMutation({ mutationFn: () => api.post<Detail>(`/conversations/${id}/close`, { reason: 'resolved' }), onSuccess: () => done('Conversation closed.') });

  if (detail.isLoading) return <Card><LoadingRows rows={4} /></Card>;
  if (detail.error || !detail.data) return <ErrorState error={detail.error} retry={() => void detail.refetch()} />;
  const d = detail.data;
  const closed = d.status === 'closed';
  const blocked = (checked?.length ?? 0) > 0;
  const error = replyM.error ?? note.error ?? close.error;
  return (
    <Card>
      <div data-testid="conversation-detail">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-[26px] font-semibold leading-tight text-navy">{d.patientName}</h2>
            <p className="mt-1 text-[14px] text-[#5b6b7c]">{d.patientLine}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {d.clinical && <span className={`${chip} bg-alert-soft font-semibold text-alert`}>Clinical</span>}
            <span className={`${chip} bg-mint font-semibold text-forest`}>{STATUS_TEXT[d.status] ?? d.status}</span>
            {d.prescriber && <span className={`${chip} bg-[#efeee9] font-semibold text-navy`}>{d.prescriber}</span>}
          </div>
        </div>
        {d.contactPref === 'call' && (
          <p className="mt-3 rounded-[10px] border border-[#ecd9a0] bg-risk-yellow-soft px-3 py-2 text-[13.5px] text-risk-yellow">
            Asked for a call{d.patientPhone ? `: ${d.patientPhone}` : ', and has no phone on file.'}
          </p>
        )}

        {d.clinical && (
          <div className="mt-3">
            {recording ? (
              <div className="rounded-[12px] border border-hairline bg-[#fbfaf7] p-4">
                <p className="mb-3 text-[13.5px] font-semibold text-navy">Record a side effect (LR-18)</p>
                <RecordSideEffect
                  patientId={d.patientId}
                  source="message"
                  sourceRef={d.id}
                  prefill={d.messages.filter((m) => m.sender === 'patient').map((m) => m.body).join('\n')}
                  onDone={() => {
                    setRecording(false);
                    setFlash('Side effect recorded on the patient profile.');
                  }}
                />
              </div>
            ) : (
              <button type="button" className="text-[13.5px] font-semibold text-forest underline" onClick={() => setRecording(true)}>
                Record a side effect from this conversation
              </button>
            )}
          </div>
        )}

        <div className="mt-4 space-y-2.5 rounded-[12px] bg-[#f3f2ed] p-3" data-testid="conversation-messages">
          <p className="px-1 text-[12px] font-semibold text-[#5b6b7c]">{d.topicLabel}</p>
          {d.messages.map((m) => (
            <div
              key={m.id}
              className={`max-w-[88%] rounded-[10px] border px-3 py-2.5 ${m.sender === 'patient' ? 'border-hairline bg-white' : m.internal ? 'ml-auto border-[#ecd9a0] bg-[#fff8e3]' : m.sender === 'system' ? 'mx-auto border-hairline bg-[#fbfaf7]' : 'ml-auto border-[#cfe3d8] bg-[#eaf4ee]'}`}
            >
              <p className="text-[11.5px] font-semibold text-[#4d6072]">
                {m.from}
                {m.internal ? ' · internal note, never shown to the patient' : ''} · {at(m.at)}
              </p>
              <p className="mt-1 whitespace-pre-line text-[14px] text-navy">{m.body}</p>
              {/* MS-014: each view is fetched through the API and logged. */}
              {m.photos && m.photos.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="inbox-photos">
                  {m.photos.map((p, i) => <PhotoThumb key={p.id} load={() => api.downloadPdf(`/conversations/photos/${p.id}`)} mime={p.mime} label={`Photo ${i + 1} from ${m.from}`} size={112} />)}
                </div>
              )}
            </div>
          ))}
        </div>

        {!closed && (
          <>
            <div className="mt-5 flex flex-wrap items-baseline justify-between gap-2">
              <label htmlFor="reply-text" className={label}>Your reply</label>
              <span className="text-[12.5px] text-[#5b6b7c]">Suggested replies arrive once an AI service is covered by a BAA.</span>
            </div>
            <textarea id="reply-text" className={`${inputClass} mt-2 min-h-[120px] text-[15px] leading-relaxed`} value={text} onChange={(e) => setText(e.target.value)} />
            {checked && (
              <p className={`mt-2 text-[13px] ${blocked ? 'text-alert' : 'text-[#4d6072]'}`} role="status" data-testid="reply-check">
                {blocked ? `Change "${checked[0]!.phrase}": ${checked[0]!.why}` : 'Passed the wording check. Edit before sending.'}
              </p>
            )}
            {error && <p className="mt-2 text-sm text-red-600" role="alert">{error instanceof Error ? error.message : 'Could not do that'}</p>}
            {flash && <p className="mt-2 text-[13px] text-forest" role="status">{flash}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button disabled={!text.trim() || blocked} loading={replyM.isPending} onClick={() => replyM.mutate('portal')}>Send reply</Button>
              <Button variant="secondary" disabled title="The pharmacy portal is not switched on yet">Send to pharmacy</Button>
              <Button variant="secondary" disabled={!text.trim() || blocked} loading={replyM.isPending} onClick={() => replyM.mutate('call')}>Offer a call</Button>
              <Button variant="secondary" disabled={!text.trim()} loading={note.isPending} onClick={() => note.mutate()}>Internal note</Button>
              <button type="button" className="px-2 text-[14px] font-semibold text-[#4d6072] hover:text-navy" onClick={() => close.mutate()} disabled={close.isPending}>Close</button>
              <InfoHint label="About replies">
                The reply shows in the patient's app. Text alerts start once the texting service has its BAA. Internal notes never reach the patient. Send to pharmacy arrives with the pharmacy portal.
              </InfoHint>
            </div>
          </>
        )}
        {closed && <p className="mt-4 text-[13.5px] text-[#5b6b7c]">Closed. The patient starts a new message for anything else.</p>}
      </div>
    </Card>
  );
}
